import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  PROFILES,
  PHASES,
  BLOCKED_ITEMS,
  RECENTLY_COMPLETED,
  COMAN_CRITERIA,
  COMAN_GUIDANCE,
  COMAN_DOCS,
  NDA_IP,
  PACKAGING_GUIDANCE,
  TESTING_GUIDANCE,
  REGULATORY_GUIDANCE,
  TEMPLATES,
} from "@/compass/data";
import { COMAN_DIRECTORY } from "@/compass/comanDirectory";
import { TESTING_DIRECTORY } from "@/compass/testingDirectory";
import { PACKAGING_DIRECTORY } from "@/compass/packagingDirectory";
import { REGULATORY_DIRECTORY } from "@/compass/regulatoryDirectory";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

import { Roadmap } from "@/compass/Roadmap";
import { PlanningDetails } from "@/compass/PlanningDetails";
import { detailExportRows } from "@/compass/planningDetails";
import { useCompassProgress } from "@/compass/useCompassProgress";
import { loadDrafts, loadDraftMeta, saveDraftsLocally, canSyncDrafts, mergeDraftsWithServer, markDraftSynced, loadDraftOwner, loadDraftSynced } from "@/compass/draftStore";

const REG_KIND_LABELS = { extension: "Extension program", licensing: "Licensing / permits", federal: "Federal" };

const LAB_TYPE_LABELS = { university: "University program", "commercial-lab": "Commercial lab", "state-program": "State program" };

const PROFILE_KEY = "alaine_compass_profile_v1";
const DRAFT_KEY = "alaine_compass_drafts_v1";
const PUB = process.env.PUBLIC_URL || "";

const TABS = [
  { id: "home", label: "Home" },
  { id: "profiles", label: "Profiles" },
  { id: "roadmap", label: "Roadmap" },
  { id: "resources", label: "Resources" },
  { id: "templates", label: "Templates" },
  { id: "guides", label: "My guides" },
  { id: "how", label: "How it works" },
];


// Auto-retry pacing for a failed workbook sync: first retry after 3s, then
// doubling up to 60s between attempts. The browser "online" event short-circuits
// the wait and retries immediately.
const SYNC_RETRY_BASE_MS = 3000;
const SYNC_RETRY_MAX_MS = 60000;

function StatusBadge({ status }) {
  const map = {
    done: ["DONE", "bg-emerald-100 text-emerald-800"],
    today: ["TODAY", "bg-emerald-800 text-white"],
    next: ["NEXT", "bg-amber-100 text-amber-800"],
    later: ["LATER", "bg-stone-100 text-stone-500"],
  };
  const [label, cls] = map[status] || map.later;
  return <span className={`text-[10px] font-bold tracking-wider px-2 py-1 rounded-full ${cls}`}>{label}</span>;
}

function formatResult(value, format) {
  if (format === "text") return String(value ?? "—");
  const n = Number(value) || 0;
  if (format === "money") return `$${n.toFixed(2)}`;
  if (format === "percent") return `${(n * 100).toFixed(1)}%`;
  return `${Math.round(n * 10) / 10}`;
}

function ResultRows({ results, derived }) {
  return (
    <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4">
      <div className="text-[10px] font-bold tracking-widest text-emerald-800 uppercase mb-2">Calculated for you — nothing to type here</div>
      {results.map((r) => (
        <div key={r.key + r.label} className="flex items-baseline justify-between gap-3 py-1" data-testid={`workbook-result-${r.key}`}>
          <div className="min-w-0">
            <span className={`text-sm ${r.strong ? "font-bold text-stone-900" : "font-medium text-stone-800"}`}>{r.label}</span>
            {r.note && <span className="block text-xs text-stone-500">{r.note}</span>}
          </div>
          <span className={`shrink-0 tabular-nums ${r.strong ? "text-base font-bold text-emerald-900" : "text-sm font-semibold text-emerald-800"}`}>
            {formatResult(derived[r.key], r.format)}
          </span>
        </div>
      ))}
    </div>
  );
}

function WorkbookTable({ table, rows, onChange }) {
  const blankRow = () => table.columns.reduce((o, c) => ({ ...o, [c.id]: "" }), {});
  const list = rows && rows.length ? rows : (table.starterRows || [{}]).map((r) => ({ ...blankRow(), ...r }));
  const update = (i, colId, val) => {
    const next = list.map((r, j) => (j === i ? { ...r, [colId]: val } : r));
    onChange(next);
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr>
            {table.columns.map((c) => (
              <th key={c.id} className="text-left text-xs font-bold text-stone-600 pb-1.5 pr-2 whitespace-nowrap">{c.label}</th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {list.map((row, i) => (
            <tr key={i}>
              {table.columns.map((c) => (
                <td key={c.id} className="pr-2 pb-2">
                  <input
                    type="text"
                    inputMode={c.type === "text" ? undefined : "decimal"}
                    value={row[c.id] ?? ""}
                    onChange={(e) => update(i, c.id, e.target.value)}
                    className="w-full min-w-[6rem] rounded-md border border-stone-200 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-700/40"
                  />
                </td>
              ))}
              <td className="pb-2">
                <button type="button" aria-label="Remove row" onClick={() => onChange(list.filter((_, j) => j !== i))}
                  className="h-7 w-7 rounded-full text-stone-400 hover:text-red-600 hover:bg-red-50 font-bold">×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" onClick={() => onChange([...list, blankRow()])}
        className="mt-1 text-sm font-semibold text-emerald-800 hover:text-emerald-700">+ {table.addLabel || "Add row"}</button>
    </div>
  );
}

// Escape a value for a CSV cell (wrap when it holds a comma, quote, or newline).
function csvCell(v) {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Builds a CSV export for a workbook template: one block per section with
// entered fields, table rows, and calculated results. Exported for tests.
export function buildWorkbookCsv(template, values, derived) {
  const rows = [
    [`${template.name} — Alaine's Compass`],
    [`Saved ${new Date().toLocaleDateString()}`],
    [],
  ];
  template.sections.forEach((s) => {
    rows.push([s.title.toUpperCase()]);
    (s.fields || []).forEach((f) => {
      rows.push([f.unit ? `${f.label} (${f.unit})` : f.label, values[f.id] || ""]);
    });
    if (s.table) {
      rows.push(s.table.columns.map((c) => c.label));
      const list = Array.isArray(values[s.table.id]) ? values[s.table.id] : [];
      list.forEach((row) => {
        rows.push(s.table.columns.map((c) => row[c.id] || ""));
      });
    }
    (s.results || []).forEach((r) => {
      rows.push([r.label, formatResult(derived[r.key], r.format), "calculated"]);
    });
    rows.push([]);
  });
  rows.push(...detailExportRows(template.id, values));
  return rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
}

export function TemplateModal({ template, onClose }) {
  const { user } = useAuth();
  const [values, setValues] = useState(() => loadDrafts()[template.id] || {});
  const [saved, setSaved] = useState(false);
  // null = nothing saved this session yet; otherwise { status: "synced" | "local" | "failed", at: ISO string }
  const [sync, setSync] = useState(null);
  const isWorkbook = Array.isArray(template.sections);
  const derived = isWorkbook && template.compute ? template.compute(values) : {};

  // A save that couldn't reach the account retries automatically: immediately
  // when connectivity returns (window "online") and on a backoff timer while
  // the modal stays open. The merge endpoint's updated_at newer-wins logic
  // stays authoritative, so a retry can never duplicate or clobber work.
  useEffect(() => {
    if (!sync || sync.status !== "failed" || !canSyncDrafts(user)) return undefined;
    let cancelled = false;
    let timer = null;
    let delay = SYNC_RETRY_BASE_MS;
    const attempt = () => {
      if (cancelled) return;
      mergeDraftsWithServer(user)
        .then(() => {
          if (!cancelled) setSync({ status: "synced", at: new Date().toISOString() });
        })
        .catch(() => {
          if (cancelled) return;
          delay = Math.min(delay * 2, SYNC_RETRY_MAX_MS);
          timer = setTimeout(attempt, delay);
        });
    };
    const onOnline = () => {
      if (timer) clearTimeout(timer);
      delay = SYNC_RETRY_BASE_MS;
      attempt();
    };
    window.addEventListener("online", onOnline);
    timer = setTimeout(attempt, delay);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener("online", onOnline);
    };
  }, [sync, user]);

  const save = () => {
    const now = new Date().toISOString();
    const drafts = loadDrafts();
    const meta = loadDraftMeta();
    const before = drafts[template.id];
    drafts[template.id] = values;
    meta[template.id] = now;
    saveDraftsLocally(drafts, meta, canSyncDrafts(user) ? user.id : undefined);
    window.dispatchEvent(new CustomEvent("compass-worksheet-saved", { detail: { templateId: template.id, before, after: values } }));
    // Sync to the founder's account so the draft follows them across devices.
    // localStorage above is the offline fallback — a failed sync never loses work.
    if (canSyncDrafts(user)) {
      try {
        Promise.resolve(api.put(`/compass/drafts/${template.id}`, { values, updated_at: now }, { skipNetToast: true }))
          .then(() => {
            markDraftSynced(template.id, now);
            setSync({ status: "synced", at: now });
          })
          .catch(() => {
            // offline / expired session — local copy re-merges on next login
            setSync({ status: "failed", at: now });
          });
      } catch {
        setSync({ status: "failed", at: now });
      }
    } else {
      setSync({ status: "local", at: now });
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const download = () => {
    let content, type, ext;
    if (isWorkbook) {
      // Workbooks export as a real spreadsheet (CSV) advisors and lenders can open.
      content = buildWorkbookCsv(template, values, derived);
      type = "text/csv";
      ext = "csv";
    } else {
      const lines = [`${template.name} — Alaine's Compass`, `Saved ${new Date().toLocaleDateString()}`, ""];
      template.fields.forEach((f) => {
        lines.push(f.toUpperCase());
        lines.push(values[f] || "(not filled in)");
        lines.push("");
      });
      detailExportRows(template.id, values).forEach((row) => lines.push(row.join(" | ")));
      content = lines.join("\n");
      type = "text/plain";
      ext = "txt";
    }
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${template.id}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4 sm:p-8" onClick={onClose}>
      <div className={`bg-white rounded-xl shadow-xl ${isWorkbook ? "max-w-3xl" : "max-w-2xl"} w-full my-4`} onClick={(e) => e.stopPropagation()}>
        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs font-bold tracking-widest text-emerald-800 uppercase">Fillable template</div>
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 mt-1">{template.name}</h2>
            </div>
            <button onClick={onClose} aria-label="Close" className="h-8 w-8 rounded-full bg-stone-100 hover:bg-stone-200 grid place-items-center text-stone-600 font-bold shrink-0">×</button>
          </div>
          {isWorkbook ? (
            <>
              {template.intro && <p className="mt-4 text-sm text-stone-600">{template.intro}</p>}
              {template.sections.map((s) => (
                <div key={s.id} className="mt-6 rounded-lg bg-emerald-50/60 border border-emerald-100 p-4 sm:p-6" data-testid={`workbook-section-${s.id}`}>
                  <div className="font-bold text-stone-900">{s.title}</div>
                  {s.intro && <p className="mt-1 mb-4 text-sm text-stone-600">{s.intro}</p>}
                  {(s.fields || []).map((f) => (
                    <div key={f.id} className="mb-4">
                      <label htmlFor={f.id} className="block font-semibold text-sm text-stone-900 mb-0.5">
                        {f.label}
                        {f.unit && <span className="ml-1.5 font-normal text-xs text-stone-500">{f.unit}</span>}
                      </label>
                      {f.tip && <div className="text-xs text-stone-500 mb-1.5">{f.tip}</div>}
                      <input
                        id={f.id}
                        type="text"
                        inputMode={f.type === "text" ? undefined : "decimal"}
                        value={values[f.id] || ""}
                        onChange={(e) => setValues((v) => ({ ...v, [f.id]: e.target.value }))}
                        className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-700/40"
                      />
                    </div>
                  ))}
                  {s.table && (
                    <WorkbookTable
                      table={s.table}
                      rows={values[s.table.id]}
                      onChange={(rows) => setValues((v) => ({ ...v, [s.table.id]: rows }))}
                    />
                  )}
                  {s.results && s.results.length > 0 && <ResultRows results={s.results} derived={derived} />}
                </div>
              ))}
            </>
          ) : (
            <div className="mt-6 rounded-lg bg-emerald-50/60 border border-emerald-100 p-4 sm:p-6">
              <div className="font-bold text-stone-900 mb-4">Fill this in</div>
              {template.fields.map((f) => (
                <div key={f} className="mb-4">
                  <label className="block font-semibold text-sm text-stone-900 mb-1.5">{f}</label>
                  <textarea
                    rows={2}
                    value={values[f] || ""}
                    onChange={(e) => setValues((v) => ({ ...v, [f]: e.target.value }))}
                    className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-700/40"
                  />
                </div>
              ))}
            </div>
          )}
          <PlanningDetails templateId={template.id} values={values} onChange={setValues} />
          <p className="mt-3 text-xs text-stone-600">Changing a saved worksheet flags affected completed stages for reconfirmation. Your other work is retained.</p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button onClick={save} className="px-5 py-2.5 rounded-full bg-emerald-800 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors">
              {saved ? "Saved ✓" : "Save draft"}
            </button>
            <button onClick={download} className="px-5 py-2.5 rounded-full border border-stone-300 hover:border-emerald-800 text-stone-800 text-sm font-semibold transition-colors">
              Download filled template
            </button>
            <SyncStatus sync={sync} canSync={canSyncDrafts(user)} userId={user?.id} templateId={template.id} />
          </div>
        </div>
      </div>
    </div>
  );
}

// "just now", "5 min ago", "2:14 PM", or a date for older syncs.
function formatSyncTime(iso) {
  const t = new Date(iso).getTime();
  if (!iso || Number.isNaN(t)) return "";
  const diffMin = Math.floor((Date.now() - t) / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

// Footer indicator: synced to account (with time), saved on this device only,
// or sync failed (work is safe locally and re-merges on next save/login).
function SyncStatus({ sync, canSync, userId, templateId }) {
  if (!sync) {
    // Stored sync timestamps only count when the cached drafts belong to the
    // signed-in user — a previous user's syncs on a shared browser don't.
    if (canSync && userId && loadDraftOwner() === userId) {
      const syncedAt = loadDraftSynced()[templateId];
      const savedAt = loadDraftMeta()[templateId];
      // Only claim "synced" when the last successful sync is at least as new
      // as the last local save — a failed/offline save must not read as synced.
      if (syncedAt && (!savedAt || syncedAt >= savedAt)) {
        return (
          <span className="text-xs text-stone-500" data-testid="draft-sync-status">
            Synced to your account · {formatSyncTime(syncedAt)}
          </span>
        );
      }
      if (savedAt) {
        return (
          <span className="text-xs text-amber-700" data-testid="draft-sync-status">
            Saved on this device — not yet synced to your account. It'll sync on your next save or login.
          </span>
        );
      }
    }
    return (
      <span className="text-xs text-stone-500" data-testid="draft-sync-status">
        {canSync ? "Drafts sync to your account when you save." : "Drafts save in this browser only."}
      </span>
    );
  }
  if (sync.status === "synced") {
    return (
      <span className="text-xs text-emerald-700" data-testid="draft-sync-status">
        ✓ Synced to your account · {formatSyncTime(sync.at)}
      </span>
    );
  }
  if (sync.status === "failed") {
    return (
      <span className="text-xs text-amber-700" data-testid="draft-sync-status">
        Saved on this device — couldn't reach your account. It'll sync on your next save or login.
      </span>
    );
  }
  return (
    <span className="text-xs text-stone-500" data-testid="draft-sync-status">
      Saved on this device only — sign in to sync to your account.
    </span>
  );
}

function Stars({ value, onChange, size = "text-base" }) {
  // Static display when no onChange; interactive picker otherwise.
  return (
    <span className="inline-flex items-center gap-0.5" role={onChange ? "radiogroup" : undefined}>
      {[1, 2, 3, 4, 5].map((n) => (
        onChange ? (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            data-testid={`coman-rate-star-${n}`}
            className={`${size} leading-none ${n <= value ? "text-amber-500" : "text-stone-300"} hover:scale-110 transition-transform`}
          >
            ★
          </button>
        ) : (
          <span key={n} className={`${size} leading-none ${n <= Math.round(value) ? "text-amber-500" : "text-stone-300"}`}>★</span>
        )
      ))}
    </span>
  );
}

function ComanReviews({ entry, user, summary, onSaved }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isMember = user && user.role !== "demo";

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/compass/coman-reviews/${entry.id}`, { skipNetToast: true });
      setReviews(data.reviews || []);
      if (data.mine) { setRating(data.mine.rating || 0); setNote(data.mine.review || ""); }
    } catch {
      setError("Couldn't load reviews right now.");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!rating) { setError("Pick a star rating first."); return; }
    setSaving(true);
    setError("");
    try {
      await api.post("/compass/coman-reviews", { coman_id: entry.id, rating, review: note });
      const { data } = await api.get(`/compass/coman-reviews/${entry.id}`, { skipNetToast: true });
      setReviews(data.reviews || []);
      onSaved();
    } catch (err) {
      if (!err.handledToast) setError("Couldn't save your review. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 border-t border-stone-100 pt-3">
      <div className="flex items-center justify-between gap-2">
        {summary ? (
          <span className="flex items-center gap-1.5 text-sm" data-testid={`coman-rating-${entry.id}`}>
            <Stars value={summary.avg} size="text-sm" />
            <span className="font-semibold text-stone-800">{summary.avg}</span>
            <span className="text-xs text-stone-400">({summary.count} review{summary.count > 1 ? "s" : ""})</span>
          </span>
        ) : (
          <span className="text-xs text-stone-400">No member reviews yet</span>
        )}
        {isMember ? (
          <button onClick={toggle} data-testid={`coman-rate-toggle-${entry.id}`}
            className="text-xs font-semibold text-emerald-800 hover:underline shrink-0">
            {open ? "Close" : summary ? "Rate / read reviews" : "Be the first to rate"}
          </button>
        ) : (
          <Link to="/register" className="text-xs font-semibold text-emerald-800 hover:underline shrink-0">
            Sign in to rate
          </Link>
        )}
      </div>
      {open && isMember && (
        <div className="mt-3 rounded-lg bg-stone-50 border border-stone-200 p-3">
          <div className="text-xs font-bold tracking-wider text-stone-500 uppercase">Your review</div>
          <div className="mt-1.5"><Stars value={rating} onChange={setRating} size="text-xl" /></div>
          <textarea
            rows={2}
            maxLength={600}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What was working with them like? MOQs, communication, quality…"
            data-testid={`coman-review-note-${entry.id}`}
            className="mt-2 w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-700/40"
          />
          <div className="mt-2 flex items-center gap-3">
            <button onClick={save} disabled={saving} data-testid={`coman-review-save-${entry.id}`}
              className="px-4 py-1.5 rounded-full bg-emerald-800 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-semibold transition-colors">
              {saving ? "Saving…" : "Save review"}
            </button>
            {error && <span className="text-xs text-red-600">{error}</span>}
          </div>
          <div className="mt-3">
            {loading ? (
              <div className="text-xs text-stone-400">Loading reviews…</div>
            ) : reviews.length > 0 ? (
              <div className="space-y-2">
                {reviews.map((r, i) => (
                  <div key={i} className="rounded-md bg-white border border-stone-200 p-2.5">
                    <div className="flex items-center gap-2">
                      <Stars value={r.rating} size="text-xs" />
                      <span className="text-xs font-semibold text-stone-700">{r.reviewer_name}</span>
                    </div>
                    {r.review && <p className="mt-1 text-xs text-stone-600 leading-relaxed">{r.review}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-stone-400">No reviews yet — yours will be the first.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export { mergeDraftsWithServer, hasPendingDraftSync, canSyncDrafts } from "@/compass/draftStore";
export default function Compass() {
  const [tab, setTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      return TABS.some((x) => x.id === t) ? t : "home";
    } catch { return "home"; }
  });
  const [progress, setProgress, saveStatus] = useCompassProgress();
  const profileId = progress.profileId || "dry-seasoning";
  const setProfileId = (id) => setProgress((p) => ({ ...p, profileId: id }));
  const [resourceTab, setResourceTab] = useState("coman");
  const [dirState, setDirState] = useState("all");
  const [labType, setLabType] = useState("all");
  const [labState, setLabState] = useState("all");
  const [regState, setRegState] = useState("all");
  const [openTemplate, setOpenTemplate] = useState(null);
  const { user } = useAuth();
  const [comanRatings, setComanRatings] = useState({});
  const [guideLibrary, setGuideLibrary] = useState({ guides: [], hasGuideBundle: false, loading: true, error: "" });
  const [downloadingGuide, setDownloadingGuide] = useState(null);

  const refreshComanRatings = () => {
    api.get("/compass/coman-reviews", { skipNetToast: true })
      .then(({ data }) => setComanRatings(data || {}))
      .catch(() => { /* ratings are progressive enhancement — cards still render */ });
  };
  useEffect(() => { refreshComanRatings(); }, []);

  useEffect(() => {
    let active = true;
    api.get("/compass/guides", { skipNetToast: true })
      .then(({ data }) => {
        if (!active) return;
        setGuideLibrary({
          guides: Array.isArray(data?.guides) ? data.guides : [],
          hasGuideBundle: !!data?.has_guide_bundle,
          loading: false,
          error: "",
        });
      })
      .catch(() => {
        if (active) setGuideLibrary((current) => ({
          ...current,
          loading: false,
          error: "We couldn't load your guide library. Please try again.",
        }));
      });
    return () => { active = false; };
  }, [user?.id]);

  const downloadGuide = async (guide) => {
    if (!guide?.download_url || downloadingGuide) return;
    setDownloadingGuide(guide.id);
    try {
      const authenticatedUrl = new URL(guide.download_url, window.location.origin).href;
      const { data, headers } = await api.get(authenticatedUrl, { responseType: "blob", skipNetToast: true });
      const url = URL.createObjectURL(new Blob([data], { type: headers?.["content-type"] || "application/pdf" }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${guide.id}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch {
      setGuideLibrary((current) => ({ ...current, error: "That guide could not be downloaded. Please try again." }));
    } finally {
      setDownloadingGuide(null);
    }
  };

  // Pull the founder's saved drafts down and push any local-only ones up as
  // soon as we know who they are, so workbook numbers survive device switches.
  useEffect(() => {
    if (!canSyncDrafts(user)) return;
    mergeDraftsWithServer(user).catch(() => { /* offline — local cache still works */ });
  }, [user?.id]);

  // The "connectivity is back" retry listener lives app-wide now (see
  // DraftSyncOnline in App.js), so a pending workbook save still syncs even
  // after the founder navigates away from this page.

  useEffect(() => {
    try { localStorage.setItem(PROFILE_KEY, profileId); } catch { /* ignore */ }
  }, [profileId]);

  useEffect(() => { window.scrollTo(0, 0); }, [tab]);
  useEffect(() => { setDirState("all"); }, [profileId]);
  useEffect(() => { setLabState("all"); }, [labType]);

  const profile = useMemo(() => PROFILES.find((p) => p.id === profileId) || PROFILES[0], [profileId]);
  const comanList = COMAN_GUIDANCE[profileId] || COMAN_GUIDANCE.default;
  const dirMatches = useMemo(
    () => COMAN_DIRECTORY.filter((e) => e.profiles.includes(profileId)),
    [profileId]
  );
  const dirStates = useMemo(
    () => [...new Set(dirMatches.map((e) => e.state))].sort(),
    [dirMatches]
  );
  const dirFiltered = dirState === "all" ? dirMatches : dirMatches.filter((e) => e.state === dirState);
  const labTypeMatches = useMemo(
    () => (labType === "all" ? TESTING_DIRECTORY : TESTING_DIRECTORY.filter((e) => e.type === labType)),
    [labType]
  );
  const labStates = useMemo(
    () => [...new Set(labTypeMatches.map((e) => e.state).filter(Boolean))].sort(),
    [labTypeMatches]
  );
  const labFiltered = labState === "all" ? labTypeMatches : labTypeMatches.filter((e) => e.state === labState);
  const regStates = useMemo(
    () => [...new Set(REGULATORY_DIRECTORY.map((e) => e.state).filter(Boolean))].sort(),
    []
  );
  const regFiltered = useMemo(
    () => REGULATORY_DIRECTORY.filter((e) => e.kind === "federal" || regState === "all" || e.state === regState),
    [regState]
  );

  return (
    <div className="platform-blue min-h-screen bg-stone-50">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-stone-200 bg-white/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="h-8 w-8 rounded-md bg-emerald-800 text-white grid place-items-center font-bold">A</div>
            <div className="leading-none">
              <div className="font-bold text-base tracking-tight">Alaine's Compass<sup className="text-[0.5em] font-semibold ml-0.5 align-super">™</sup></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mt-0.5">Founder guided workspace</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`compass-tab-${t.id}`}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.id ? "border-emerald-700 text-emerald-800" : "border-transparent text-stone-600 hover:text-stone-900"
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
          <a href="/alaine-co/project-intake" className="hidden sm:inline-flex px-4 py-2 rounded-full bg-emerald-800 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors shrink-0">
            Work with Alaine & Co
          </a>
        </div>
        {/* A labeled control stays discoverable without clipped horizontal tabs. */}
        <div className="md:hidden border-t border-stone-100 px-4 py-2">
          <label className="flex items-center gap-3 text-sm font-semibold text-stone-700">
            <span>Menu</span>
            <select value={tab} onChange={(event) => setTab(event.target.value)}
              className="min-w-0 flex-1 rounded-md border border-stone-300 bg-white px-3 py-2 font-medium text-stone-800"
              aria-label="Compass menu">
              {TABS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </label>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* HOME */}
        {tab === "home" && (
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] text-stone-900">
                Every food product follows a different path to <span className="text-emerald-800">commercialization.</span>
              </h1>
              <p className="mt-6 text-stone-600 text-lg leading-relaxed">
                Alaine's Compass builds the roadmap for yours. It turns founder uncertainty into a
                clear sequence of decisions, documents, tests, and commercialization moves.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={() => setTab("roadmap")} data-testid="compass-cta-profiles"
                  className="px-6 py-3 rounded-full bg-emerald-800 hover:bg-emerald-700 text-white font-semibold text-sm transition-colors">
                  {progress.productName ? "Continue your roadmap" : "Start your guided roadmap"}
                </button>
                <button onClick={() => setTab("roadmap")}
                  className="px-6 py-3 rounded-full border border-stone-300 hover:border-emerald-800 text-stone-800 font-semibold text-sm transition-colors">
                  View your stages
                </button>
                <a href="/alaine-co/project-intake"
                  className="px-6 py-3 rounded-full border border-emerald-800 text-emerald-800 hover:bg-emerald-50 font-semibold text-sm transition-colors">
                  Work with Alaine & Co
                </a>
              </div>
              <div className="mt-10 rounded-lg bg-emerald-50 border border-emerald-100 p-4 text-sm text-stone-700">
                <span className="font-semibold text-emerald-900">One step at a time:</span>{" "}
                Tell us what you are making, choose your starting stage, and work through practical guidance and worksheets. Your progress is saved so you can return where you left off.
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg border border-stone-200">
              <img src={`${PUB}/images/compass/profile-${profile.id}.png`} alt={profile.name} className="w-full h-full object-cover" />
            </div>
          </div>
        )}

        {/* PROFILES */}
        {tab === "profiles" && (
          <div>
            <div className="inline-block text-[10px] font-bold tracking-widest text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1 uppercase">Profiles</div>
            <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-stone-900">What are you making?</h1>
                <p className="mt-2 text-stone-600">Pick the profile first. Everything else changes from this answer.</p>
              </div>
              <button onClick={() => setTab("roadmap")} data-testid="compass-use-profile"
                className="px-5 py-2.5 rounded-full bg-emerald-800 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors">
                Use selected profile →
              </button>
            </div>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {PROFILES.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setProfileId(p.id)}
                  data-testid={`compass-profile-${p.id}`}
                  className={`text-left rounded-xl bg-white border-2 overflow-hidden transition-all hover:shadow-md ${
                    profileId === p.id ? "border-emerald-700 ring-2 ring-emerald-700/20" : "border-stone-200 hover:border-stone-300"
                  }`}
                >
                  <div className="p-4 pb-0">
                    <span className="text-[10px] font-bold tracking-widest text-emerald-800">{p.tag}</span>
                  </div>
                  <div className="p-4 pt-2">
                    <div className="rounded-lg overflow-hidden bg-stone-100 aspect-[4/3]">
                      <img src={`${PUB}/images/compass/${p.img}`} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                    <div className="mt-3 font-bold text-stone-900 leading-snug">{p.name}</div>
                    <div className="mt-1.5 text-sm text-stone-500 leading-relaxed">{p.blurb}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ROADMAP */}
        {tab === "roadmap" && (
          <div className="animate-in fade-in">
            <Roadmap key={user?.id || "anonymous"} progress={progress} setProgress={setProgress} saveStatus={saveStatus} openTemplateModal={setOpenTemplate} openResources={(section) => { setResourceTab(section); setTab("resources"); }} />
          </div>
        )}

        {/* RESOURCES */}
        {tab === "resources" && (
          <div>
            <div className="inline-block text-[10px] font-bold tracking-widest text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1 uppercase">Resources</div>
            <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-stone-900">Real guidance, not generic links.</h1>
                <p className="mt-2 text-stone-600 max-w-2xl">
                  Concrete co-man starting points, documents, NDA/IP, packaging, testing, and regulatory guidance.
                </p>
              </div>
              <select
                value={profileId}
                onChange={(e) => setProfileId(e.target.value)}
                className="rounded-md border border-stone-300 bg-white px-3 py-2 text-sm"
                data-testid="compass-resources-profile-select"
              >
                {PROFILES.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div className="mt-8 grid lg:grid-cols-[200px,1fr] gap-6 items-start">
              <div className="flex lg:flex-col gap-2 overflow-x-auto">
                {[["coman", "Co-Man Fit"], ["packaging", "Packaging"], ["testing", "Testing Labs"], ["regulatory", "Regulatory"]].map(([id, label]) => (
                  <button key={id} onClick={() => setResourceTab(id)}
                    className={`px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap text-left transition-colors ${
                      resourceTab === id ? "bg-emerald-800 text-white" : "bg-white border border-stone-200 text-stone-700 hover:border-emerald-800"
                    }`}>
                    {label}
                  </button>
                ))}
              </div>

              <div className="rounded-xl bg-white border border-stone-200 p-6 sm:p-8">
                {resourceTab === "coman" && (
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-stone-900">Co-man shortlist for {profile.name}</h2>
                    <p className="mt-2 text-sm text-stone-600">
                      Use this as a qualification guide. Do not choose by name alone — score each option by
                      category fit, reputation, MOQ, time-zone communication, and shipping distance.
                    </p>
                    <div className="mt-5 grid sm:grid-cols-5 gap-3">
                      {COMAN_CRITERIA.map((c) => (
                        <div key={c.title} className="rounded-lg border border-stone-200 p-3">
                          <div className="font-semibold text-xs text-stone-900">{c.title}</div>
                          <div className="mt-1 text-[11px] text-stone-500 leading-snug">{c.desc}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 grid sm:grid-cols-2 gap-4">
                      {comanList.map((c) => (
                        <div key={c.name} className="rounded-lg border border-stone-200 p-5">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold tracking-widest text-emerald-800 bg-emerald-50 rounded-full px-2 py-0.5">GUIDE</span>
                            <span className="font-bold text-stone-900">{c.name}</span>
                          </div>
                          <div className="mt-1 text-[11px] font-semibold text-stone-400 uppercase tracking-wider">{c.region}</div>
                          <p className="mt-2 text-sm text-stone-600 leading-relaxed">{c.notes}</p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-10 flex flex-wrap items-end justify-between gap-3">
                      <div>
                        <h3 className="text-xl font-bold text-stone-900">Co-man directory matches for {profile.name}</h3>
                        <p className="mt-1 text-sm text-stone-600">
                          {dirMatches.length} co-packers from public university and state agriculture directories.
                          Always confirm MOQs and capabilities directly — details change often.
                        </p>
                      </div>
                      <select
                        value={dirState}
                        onChange={(e) => setDirState(e.target.value)}
                        data-testid="coman-directory-state-filter"
                        className="rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-stone-700"
                      >
                        <option value="all">All states</option>
                        {dirStates.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                      {dirFiltered.map((e) => (
                        <div key={e.id} className="rounded-lg border border-stone-200 p-5" data-testid="coman-directory-card">
                          <div className="flex items-start justify-between gap-2">
                            <span className="font-bold text-stone-900">{e.name}</span>
                            <span className="shrink-0 text-[10px] font-bold tracking-widest text-stone-500 bg-stone-100 rounded-full px-2 py-0.5">
                              {e.city ? `${e.city}, ${e.state}` : e.state}
                            </span>
                          </div>
                          {e.moq && (
                            <div className="mt-1.5 text-[11px] font-semibold text-emerald-800">MOQ: {e.moq}</div>
                          )}
                          <p className="mt-2 text-sm text-stone-600 leading-relaxed">{e.notes}</p>
                          <div className="mt-3 flex items-center justify-between gap-2">
                            <span className="text-[11px] text-stone-400">Source: {e.source}</span>
                            {e.website && (
                              <a href={e.website} target="_blank" rel="noopener noreferrer"
                                className="text-xs font-semibold text-emerald-800 underline underline-offset-2">
                                Website
                              </a>
                            )}
                          </div>
                          <ComanReviews entry={e} user={user} summary={comanRatings[e.id]} onSaved={refreshComanRatings} />
                        </div>
                      ))}
                    </div>
                    {dirFiltered.length === 0 && (
                      <p className="mt-4 text-sm text-stone-500">No directory matches in that state yet — switch back to "All states".</p>
                    )}

                    <h3 className="mt-10 text-xl font-bold text-stone-900">Documents every co-man should have</h3>
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                      {COMAN_DOCS.map((d) => (
                        <div key={d.title} className="rounded-lg border border-stone-200 p-5">
                          <div className="font-bold text-stone-900">{d.title}</div>
                          <p className="mt-1.5 text-sm text-stone-600 leading-relaxed">{d.desc}</p>
                        </div>
                      ))}
                    </div>

                    <h3 className="mt-10 text-xl font-bold text-stone-900">NDA and IP rights</h3>
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                      {NDA_IP.map((d) => (
                        <div key={d.title} className="rounded-lg border border-stone-200 p-5">
                          <span className="text-[10px] font-bold tracking-widest text-emerald-800 bg-emerald-50 rounded-full px-2 py-0.5">{d.title.toUpperCase()}</span>
                          <p className="mt-2.5 text-sm text-stone-600 leading-relaxed">{d.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {resourceTab === "packaging" && (
                  <div>
                    <ResourceCards title={`Packaging guidance for ${profile.name}`} items={PACKAGING_GUIDANCE} />

                    <div className="mt-10">
                      <h3 className="text-xl font-bold text-stone-900">Packaging supplier directory</h3>
                      <p className="mt-1 text-sm text-stone-600">
                        {PACKAGING_DIRECTORY.length} suppliers that sell online in startup-friendly quantities —
                        bottles, jars, woozy bottles, pouches, and closures. Prices and minimums change — confirm directly.
                      </p>
                    </div>
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                      {PACKAGING_DIRECTORY.map((e) => (
                        <div key={e.id} className="rounded-lg border border-stone-200 p-5" data-testid="packaging-directory-card">
                          <div className="flex items-start justify-between gap-2">
                            <span className="font-bold text-stone-900">{e.name}</span>
                            {(e.city || e.state) && (
                              <span className="shrink-0 text-[10px] font-bold tracking-widest text-stone-500 bg-stone-100 rounded-full px-2 py-0.5">
                                {e.city ? `${e.city}, ${e.state}` : e.state}
                              </span>
                            )}
                          </div>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {e.products.map((p) => (
                              <span key={p} className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 rounded-full px-2 py-0.5">{p}</span>
                            ))}
                          </div>
                          <p className="mt-2 text-sm text-stone-600 leading-relaxed">{e.notes}</p>
                          {e.website && (
                            <div className="mt-3 text-right">
                              <a href={e.website} target="_blank" rel="noopener noreferrer"
                                className="text-xs font-semibold text-emerald-800 underline underline-offset-2">
                                Website
                              </a>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {resourceTab === "testing" && (
                  <div>
                    <ResourceCards title={`Testing guidance for ${profile.name}`} items={TESTING_GUIDANCE} extra={
                      <div className="mt-6 rounded-lg bg-emerald-50 border border-emerald-100 p-4 text-sm text-stone-700">
                        <span className="font-semibold text-emerald-900">Need technical support?</span>{" "}
                        <a href="/alaine-co/project-intake" className="font-semibold text-emerald-800 underline underline-offset-2">Work with Alaine &amp; Co</a>{" "}
                        on testing and label-review decisions. Alaine's Bench remains internal-only and is not public Compass access.
                      </div>
                    } />

                    <div className="mt-10 flex flex-wrap items-end justify-between gap-3">
                      <div>
                        <h3 className="text-xl font-bold text-stone-900">Testing directory</h3>
                        <p className="mt-1 text-sm text-stone-600">
                          {labTypeMatches.length} university programs, process authorities, and commercial labs
                          from public sources. Services and fees change — always confirm directly.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <select
                          value={labType}
                          onChange={(e) => setLabType(e.target.value)}
                          data-testid="testing-directory-type-filter"
                          className="rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-stone-700"
                        >
                          <option value="all">All types</option>
                          <option value="university">University programs</option>
                          <option value="commercial-lab">Commercial labs</option>
                          <option value="state-program">State programs</option>
                        </select>
                        <select
                          value={labState}
                          onChange={(e) => setLabState(e.target.value)}
                          data-testid="testing-directory-state-filter"
                          className="rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-stone-700"
                        >
                          <option value="all">All states</option>
                          {labStates.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                      {labFiltered.map((e) => (
                        <div key={e.id} className="rounded-lg border border-stone-200 p-5" data-testid="testing-directory-card">
                          <div className="flex items-start justify-between gap-2">
                            <span className="font-bold text-stone-900">{e.name}</span>
                            <span className="shrink-0 text-[10px] font-bold tracking-widest text-stone-500 bg-stone-100 rounded-full px-2 py-0.5">
                              {e.city ? `${e.city}, ${e.state}` : e.state || "National"}
                            </span>
                          </div>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            <span className="text-[10px] font-bold tracking-widest text-emerald-800 bg-emerald-50 rounded-full px-2 py-0.5">
                              {(LAB_TYPE_LABELS[e.type] || e.type).toUpperCase()}
                            </span>
                            {e.services.slice(0, 4).map((s) => (
                              <span key={s} className="text-[10px] font-semibold text-stone-500 bg-stone-100 rounded-full px-2 py-0.5">{s}</span>
                            ))}
                          </div>
                          <p className="mt-2 text-sm text-stone-600 leading-relaxed">{e.notes}</p>
                          <div className="mt-3 flex items-center justify-between gap-2">
                            <span className="text-[11px] text-stone-400">Source: {e.source}</span>
                            {e.website && (
                              <a href={e.website} target="_blank" rel="noopener noreferrer"
                                className="text-xs font-semibold text-emerald-800 underline underline-offset-2">
                                Website
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {labFiltered.length === 0 && (
                      <p className="mt-4 text-sm text-stone-500">No matches with those filters — switch back to "All".</p>
                    )}
                  </div>
                )}
                {resourceTab === "regulatory" && (
                  <div>
                    <div className="mb-10">
                      <div className="flex flex-wrap items-end justify-between gap-3">
                        <div>
                          <h3 className="text-xl font-bold text-stone-900">Who to contact in your state</h3>
                          <p className="mt-1 text-sm text-stone-600">
                            Pick your state to see your food safety extension program and licensing office,
                            plus the federal basics. Agencies and links change — verify before relying.
                          </p>
                        </div>
                        <select
                          value={regState}
                          onChange={(e) => setRegState(e.target.value)}
                          data-testid="regulatory-directory-state-filter"
                          className="rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-stone-700"
                        >
                          <option value="all">All states</option>
                          {regStates.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div className="mt-4 grid sm:grid-cols-2 gap-4">
                        {regFiltered.map((e) => (
                          <div key={e.id} className="rounded-lg border border-stone-200 p-5" data-testid="regulatory-directory-card">
                            <div className="flex items-start justify-between gap-2">
                              <span className="font-bold text-stone-900">{e.name}</span>
                              <span className="shrink-0 text-[10px] font-bold tracking-widest text-stone-500 bg-stone-100 rounded-full px-2 py-0.5">
                                {e.state || "US"}
                              </span>
                            </div>
                            <div className="mt-1.5">
                              <span className="text-[10px] font-bold tracking-widest text-emerald-800 bg-emerald-50 rounded-full px-2 py-0.5">
                                {(REG_KIND_LABELS[e.kind] || e.kind).toUpperCase()}
                              </span>
                            </div>
                            <p className="mt-2 text-sm text-stone-600 leading-relaxed">{e.notes}</p>
                            <div className="mt-3 flex items-center justify-between gap-2">
                              <span className="text-[11px] text-stone-400">Source: {e.source}</span>
                              <a href={e.website} target="_blank" rel="noopener noreferrer"
                                className="text-xs font-semibold text-emerald-800 underline underline-offset-2">
                                Website
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <ResourceCards title={`Regulatory guidance for ${profile.name}`} items={REGULATORY_GUIDANCE} extra={
                    <div className="mt-6 rounded-lg bg-emerald-50 border border-emerald-100 p-4 text-sm text-stone-700">
                      <span className="font-semibold text-emerald-900">Need a qualified review?</span>{" "}
                      <a href="/alaine-co/project-intake" className="font-semibold text-emerald-800 underline underline-offset-2">Work with Alaine &amp; Co</a>.
                      Alaine's Bench is an internal-only system and is not included with Compass.
                    </div>
                  } />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* TEMPLATES */}
        {tab === "templates" && (
          <div>
            <div className="inline-block text-[10px] font-bold tracking-widest text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1 uppercase">Templates</div>
            <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-stone-900">Templates founders can actually fill in.</h1>
            <p className="mt-2 text-stone-600">Open a template, type into fields, save the draft in this browser, or download it as a text file.</p>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {TEMPLATES.map((t) => (
                <button key={t.id} onClick={() => setOpenTemplate(t)} data-testid={`compass-template-${t.id}`}
                  className="text-left rounded-xl bg-white border border-stone-200 hover:border-emerald-700 hover:shadow-md transition-all p-6">
                  <span className="text-[10px] font-bold tracking-widest text-emerald-800 bg-emerald-50 rounded-full px-2 py-0.5">TEMPLATE</span>
                  <div className="mt-2.5 font-bold text-stone-900">{t.name}</div>
                  <p className="mt-2 text-sm text-stone-500 leading-relaxed">{t.summary}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* MY GUIDES */}
        {tab === "guides" && (
          <div>
            <div className="inline-block text-[10px] font-bold tracking-widest text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1 uppercase">My guides</div>
            <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-stone-900">Your digital guide library</h1>
            <p className="mt-2 max-w-2xl text-stone-600">Download the guides assigned to your account. Guide access is private to the signed-in owner.</p>
            {guideLibrary.error && <div role="alert" className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{guideLibrary.error}</div>}
            {guideLibrary.loading ? (
              <p className="mt-8 text-stone-500">Loading your guides…</p>
            ) : !guideLibrary.hasGuideBundle ? (
              <div className="mt-8 rounded-xl border border-stone-200 bg-white p-8">
                <h2 className="text-xl font-bold text-stone-900">Digital guides are not included in your current Compass plan.</h2>
                <p className="mt-2 text-sm text-stone-600">Your roadmap, templates, and resources remain available.</p>
              </div>
            ) : guideLibrary.guides.length === 0 ? (
              <p className="mt-8 rounded-xl border border-stone-200 bg-white p-8 text-stone-600">No guides are assigned to this account yet.</p>
            ) : (
              <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {guideLibrary.guides.map((guide) => (
                  <article key={guide.id} className="flex flex-col rounded-xl border border-stone-200 bg-white p-6">
                    <span className="text-[10px] font-bold tracking-widest text-emerald-800">DIGITAL GUIDE</span>
                    <h2 className="mt-2 text-lg font-bold text-stone-900">{guide.title}</h2>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-stone-600">{guide.description}</p>
                    <button onClick={() => downloadGuide(guide)} disabled={!!downloadingGuide || !guide.download_url}
                      className="mt-5 rounded-full bg-emerald-800 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                      data-testid={`download-guide-${guide.id}`}>
                      {downloadingGuide === guide.id ? "Preparing download…" : "Download guide"}
                    </button>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        {/* HOW IT WORKS */}
        {tab === "how" && (
          <div className="max-w-3xl">
            <div className="rounded-xl bg-white border border-stone-200 p-8 sm:p-10">
              <div className="inline-block text-[10px] font-bold tracking-widest text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1 uppercase">How it works</div>
              <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-stone-900">Your next step, explained.</h1>
              <ol className="mt-8 space-y-5">
                {[
                  ["Tell us about your product", "Name your project, choose the product type, and select where you are starting. If you are unsure, start with Concept brief.", "roadmap"],
                  ["Work through one stage", "See what to do, why it matters, and what to prepare. Open the worksheets and supporting resources directly from that stage.", "roadmap"],
                  ["Check your readiness", "Complete every exact checklist item, save the required template information or supporting evidence, and assign unresolved questions before continuing. Reopening invalidates downstream completion but retains your work.", "roadmap"],
                  ["Save and continue", "Return to your saved roadmap and continue the next unfinished stage. Templates also have their own Save draft button.", "roadmap"],
                ].map(([title, desc, target], i) => (
                  <li key={title} className="flex gap-4">
                    <div className="h-8 w-8 rounded-full bg-emerald-800 text-white grid place-items-center font-bold text-sm shrink-0">{i + 1}</div>
                    <div>
                      <button onClick={() => setTab(target)} className="font-bold text-stone-900 hover:text-emerald-800 transition-colors">{title} →</button>
                      <p className="mt-1 text-stone-600 text-sm leading-relaxed">{desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="mt-10 rounded-lg bg-emerald-50 border border-emerald-100 p-5 text-sm text-stone-700">
                <div className="font-bold text-emerald-900">Need a technical decision reviewed?</div>
                <p className="mt-1">
                  Alaine & Co. can help with formulation, testing plans, sourcing, and commercialization. Consulting is a separate service; Compass does not grant access to internal Bench tools.
                </p>
                <a href="/alaine-co/project-intake" className="mt-3 inline-flex px-5 py-2.5 rounded-full bg-emerald-800 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors">
                   Work with Alaine & Co →
                </a>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-stone-200 bg-white mt-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-wrap items-center justify-between gap-4 text-sm text-stone-500">
          <div>Alaine's Compass™ · founder guided workspace · Alaine's Bench™ is internal-only</div>
          <div className="flex gap-5">
            <a href="/alaine-co/project-intake" className="hover:text-emerald-800">Work with Alaine &amp; Co</a>
            <Link to="/terms" className="hover:text-emerald-800">Terms</Link>
            <Link to="/privacy" className="hover:text-emerald-800">Privacy</Link>
          </div>
        </div>
      </footer>

      {openTemplate && <TemplateModal template={openTemplate} onClose={() => setOpenTemplate(null)} />}
    </div>
  );
}

function ResourceCards({ title, items, extra }) {
  return (
    <div>
      <h2 className="text-2xl sm:text-3xl font-bold text-stone-900">{title}</h2>
      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        {items.map((d) => (
          <div key={d.title} className="rounded-lg border border-stone-200 p-5">
            <div className="font-bold text-stone-900">{d.title}</div>
            <p className="mt-1.5 text-sm text-stone-600 leading-relaxed">{d.desc}</p>
          </div>
        ))}
      </div>
      {extra}
    </div>
  );
}
